import styled from "styled-components";
import { Link } from 'react-router-dom';

export const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  height: 80px;
  display: flex;
  align-items: center;
`;

export const Nav = styled.nav`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  gap: 0.75rem;
`;

export const Logo = styled.img`
  height: 36px;
  width: auto;
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const LogoText = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.2;
`;

export const Tagline = styled.span`
  font-size: 0.75rem;
  color: #4a5568;
  line-height: 1.2;

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const NavList = styled.ul<{ $isOpen: boolean }>`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 1024px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    background: white;
    padding: 1rem;
    gap: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

export const NavItem = styled.li`
  @media (max-width: 1024px) {
    width: 100%;
    text-align: center;
  }
`;

export const NavLink = styled(Link)`
  color: #4a5568;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9375rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: #4F46E5;
    background: #EBF8FF;
  }

  &.active {
    color: #4F46E5;
    background: #EBF8FF;
  }

  @media (max-width: 1024px) {
    display: block;
    padding: 0.75rem;
    width: 100%;
  }
`;

export const AuthButton = styled.a`
  background: #4F46E5;
  color: white;
  padding: 0.625rem 1.25rem;
  border-radius: 0.375rem;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9375rem;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #4338CA;
    transform: translateY(-1px);
  }

  @media (max-width: 1024px) {
    width: 100%;
    text-align: center;
  }
`;

export const BurgerMenu = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #4a5568;
  font-size: 1.25rem;
  transition: color 0.2s;

  &:hover {
    color: #4F46E5;
  }

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const MobileAuthButton = styled(AuthButton)`
  display: block;
  width: 100%;
  text-align: center;
  padding: 0.75rem;
  margin: 0.5rem 0;
  font-size: 0.9rem;
  background: #CBD5E0;
  
  &:hover {
    background: #CBD5E0;
  }
`;

export const MobileNav = styled.ul<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: white;
  padding: 1rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  list-style: none;
  margin: 0;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-20px)')};
  transition: all 0.3s ease-in-out;
  height: ${({ $isOpen }) => ($isOpen ? 'auto' : '0')};
  overflow: hidden;

  @media (max-width: 768px) {
    display: block;
  }
`;

export const MobileNavItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.5rem 0;
  border-bottom: 1px solid #E2E8F0;

  &:last-child {
    border-bottom: none;
    margin-top: 1rem;
  }

  a {
    display: block;
    width: 100%;
    padding: 0.5rem 0;
  }
`;

export const GetStartedButton = styled.a`
  background: ${({ theme }) => theme.colors.secondary.main};
  color: ${({ theme }) => theme.colors.text.inverted};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary.dark};
  }
`;

export const AuthButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

export const MobileAuthButtons = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

export const RequestInviteButton = styled(Link)`
  background: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.text.inverted};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  display: inline-block;

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    text-align: center;
    margin-top: ${({ theme }) => theme.spacing.md};
  }
`;
